import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { Box, Typography, CircularProgress, IconButton, Fab } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';

// Default location (Delhi, India) and zoom level
const DEFAULT_CENTER = [28.6139, 77.209];
const DEFAULT_ZOOM = 10;
// const DEFAULT_RADIUS = 2;
const DEFAULT_RADIUS = 11;

// Function to generate a random color
const getRandomColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = 70 + Math.floor(Math.random() * 30);
  const lightness = 40 + Math.floor(Math.random() * 20);
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Cache to store colors for experts
const expertColorCache = new Map();

const MapView = ({ expertData = [], isFullscreen, setIsFullscreen}) => {
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const resizeTimeoutRef = useRef(null);

  // Initialize map once on component mount
  useEffect(() => {
    try {
      if (mapRef.current) return;

      const map = L.map('expert-map', {
        // Add these options to improve mouse interaction
        zoomControl: true,
        doubleClickZoom: true,
        closePopupOnClick: false,
        dragging: true,
        zoomSnap: 0.1,
        zoomDelta: 0.5,
        wheelPxPerZoomLevel: 60,
        trackResize: true
      }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      mapRef.current = map;

      // Add the tile layer to the map
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(map);

      // Create a marker cluster group
      const markers = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 30,
        spiderfyOnMaxZoom: true,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 16,
        iconCreateFunction: function(cluster) {
          const count = cluster.getChildCount();
          return L.divIcon({
            html: `<div style="background-color: #3498db; color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${count}</div>`,
            className: 'custom-cluster-icon',
            iconSize: [40, 40]
          });
        }
      });
      markersLayerRef.current = markers;
      map.addLayer(markers);

      // Add map interaction improvements
      map.on('zoomstart', function() {
        // Close any open popups during zoom to prevent position issues
        map.closePopup();
      });

      // Prevent context menu on right click
      map.on('contextmenu', function(e) {
        e.originalEvent.preventDefault();
      });

      // Handle touch gestures better on mobile
      map.on('movestart', function() {
        // Ensure smooth movement by closing popups
        map.closePopup();
      });

      // Add custom CSS
      const style = document.createElement('style');
      style.textContent = `
        .leaflet-popup-content-wrapper {
          width: max-content !important;
          max-width: 350px !important;
        }
        .leaflet-popup-content {
          width: max-content !important;
          max-width: 350px !important;
          margin: 13px !important;
        }
        .custom-cluster-icon {
          background: none !important;
          border: none !important;
        }
        .expert-marker {
          cursor: pointer;
        }
        .expert-marker:hover {
          opacity: 0.8;
        }
        .map-fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 9999 !important;
          background: white;
        }
        .fullscreen-controls {
          position: absolute;
          top: 20px;
          right: 20px;
          z-index: 1000;
        }
        /* Prevent touch scrolling issues on mobile in fullscreen */
        .map-fullscreen .leaflet-container {
          touch-action: pan-x pan-y;
        }
        /* Improve zoom control positioning */
        .leaflet-control-zoom {
          margin-left: 10px !important;
          margin-top: 10px !important;
        }
      `;
      document.head.appendChild(style);
      setIsLoading(false);
    } catch (err) {
      console.error("Error initializing map:", err);
      setError("Failed to initialize map");
      setIsLoading(false);
    }
  }, []);

  // Update markers whenever expertData changes
  useEffect(() => {
    const updateMarkers = () => {
      try {
        const map = mapRef.current;
        if (!map) return;
        
        const markers = markersLayerRef.current;
        markers.clearLayers();
        
        map.eachLayer((layer) => {
          if (layer instanceof L.Circle) {
            map.removeLayer(layer);
          }
        });

        expertData.forEach((expert) => {
          if (!expert.location || typeof expert.location.lat !== 'number' || typeof expert.location.lng !== 'number') {
            console.warn('Invalid location data for expert:', expert);
            return;
          }
          
          let color;
          if (expert.id) {
            if (!expertColorCache.has(expert.id)) {
              expertColorCache.set(expert.id, getRandomColor());
            }
            color = expertColorCache.get(expert.id);
          } else {
            color = getRandomColor();
          }
          
          const marker = L.circleMarker([expert.location.lat, expert.location.lng], {
            color,
            fillColor: color,
            fillOpacity: 0.8,
            radius: 8,
            weight: 2,
            className: 'expert-marker'
          });

          const popupContent = `
            <div style="font-family: Arial, sans-serif; min-width: 200px;">
              <h3 style="margin: 0 0 3px 0;"><strong>Expert:</strong> ${expert.name}</h3>
              <p style="margin: 0 0 3px 0;"><strong>Sub-Category:</strong> ${expert.allSubCategories}</p>
            </div>
          `;
          // const popupContent = `
          //   <div style="font-family: Arial, sans-serif; min-width: 200px;">
          //     <h3 style="margin: 0 0 8px 0;"><strong>Expert:</strong> ${expert.name}</h3>
          //     <p style="margin: 0 0 5px 0;"><strong>Category:</strong> ${expert?.categories[0]?.title}</p>
          //     <p style="margin: 0 0 5px 0;"><strong>Sub-Category:</strong> ${expert.allSubCategories}</p>
          //     <p style="margin: 0 0 5px 0;"><strong>Master Service:</strong> ${expert.allMasterServices}</p>
          //     <p style="margin: 0 0 5px 0;"><strong>Services:</strong> ${expert.allChildServiceTitles || 'N/A'}</p>
          //     <p style="margin: 0;"><strong>Coordinates:</strong>[${expert.location.lat || 'N/A'},${expert.location.lng || 'N/A'}]</p>
          //   </div>
          // `;

          marker.bindPopup(popupContent, {
            closeButton: true,
            autoPan: true,
            keepInView: true,
            maxWidth: 350,
            className: 'expert-popup'
          });

          // Improved event handling to prevent conflicts
          let isHovered = false;
          
          marker.on('mouseover', function(e) {
            isHovered = true;
            setTimeout(() => {
              if (isHovered && !this.isPopupOpen()) {
                this.openPopup();
              }
            }, 100);
          });

          marker.on('mouseout', function(e) {
            isHovered = false;
            setTimeout(() => {
              if (!isHovered && this.isPopupOpen()) {
                this.closePopup();
              }
            }, 200);
          });

          marker.on('click', function(e) {
            e.originalEvent.stopPropagation();
            this.openPopup();
          });
          
          markers.addLayer(marker);

          L.circle([expert.location.lat, expert.location.lng], {
            radius: DEFAULT_RADIUS * 1000,
            color,
            fillColor: color,
            fillOpacity: 0.1,
            weight: 1,
            interactive: false
          }).addTo(map);
        });

        markers.on('clusterclick', function(event) {
          // Prevent event propagation to avoid conflicts
          event.originalEvent.stopPropagation();
          
          const cluster = event.layer;
          const childMarkers = cluster.getAllChildMarkers();
          
          if (childMarkers.length <= 10) {
            cluster.spiderfy();
          } else {
            // Smooth zoom transition
            map.fitBounds(cluster.getBounds(), {
              padding: [20, 20],
              animate: true,
              duration: 0.5
            });
          }
        });

        expertData.forEach((expert, index) => {
          if (expert.location && typeof expert.location.lat === 'number') {
            const allMarkers = [];
            markers.eachLayer(function(layer) {
              allMarkers.push(layer);
            });
            if (allMarkers[index]) {
              allMarkers[index].options.expertName = expert.name;
            }
          }
        });

        if (expertData.length > 0) {
          const points = expertData
            .filter(expert => expert.location && typeof expert.location.lat === 'number')
            .map(expert => [expert.location.lat, expert.location.lng]);
          
          if (points.length > 0) {
            const bounds = L.latLngBounds(points);
            // Use setTimeout to ensure this happens after any resize operations
            setTimeout(() => {
              map.fitBounds(bounds, { 
                padding: [20, 20],
                animate: true,
                duration: 0.3
              });
            }, 150);
          }
        }
      } catch (err) {
        console.error("Error updating markers:", err);
        setError("Failed to update map markers");
      }
    };

    updateMarkers();
  }, [expertData]);

  // Handle fullscreen toggle and map resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        // Clear any existing timeout
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        
        // Debounce the resize to avoid too many calls
        resizeTimeoutRef.current = setTimeout(() => {
          try {
            mapRef.current.invalidateSize(true);
            // Force a gentle zoom to refresh the view
            const currentZoom = mapRef.current.getZoom();
            mapRef.current.setZoom(currentZoom, { animate: false });
          } catch (err) {
            console.warn('Error during map resize:', err);
          }
        }, 100);
      }
    };

    // Handle fullscreen state changes
    if (mapRef.current) {
      // Immediately invalidate size when fullscreen state changes
      setTimeout(() => {
        if (mapRef.current && mapRef.current._container) {
          handleResize();
        }
      }, 50);
    }

    // Add window resize listener for additional safety
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  // Clean up map on component unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box 
      ref={mapContainerRef}
      sx={{ 
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          backgroundColor: 'white'
        })
      }}
    >
      {/* Fullscreen Toggle Button */}
      <Fab
        size="medium"
        color="primary"
        onClick={() => {
          setIsFullscreen(!isFullscreen);
          // Small delay to ensure DOM updates before map resize
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize(true);
            }
          }, 100);
        }}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 1000,
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem'
          }
        }}
      >
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </Fab>

      {isLoading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1000
        }}>
          <CircularProgress />
        </Box>
      )}
      
      <Box 
        id="expert-map" 
        sx={{ 
          minHeight: isFullscreen ? '100vh' : '68vh', 
          width: '100%',
          height: isFullscreen ? '100vh' : 'auto'
        }} 
      />
    </Box>
  );
};

export default MapView;

// .leaflet-popup-content-wrapper {
//           width: max-content !important;
//           max-width: 350px !important;
//           overflow-wrap: break-word;
//           word-break: break-word;
//         }
//         .leaflet-popup-content {
//           width: max-content !important;
//           max-width: 350px !important;
//           margin: 13px !important;
//           overflow-wrap: break-word;
//           word-break: break-word;
//         }